import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Generic wrapper that re-renders its innerHTML when the parent product
 * dispatches a variant update. The variant-picker performs a single
 * section-render fetch and exposes the resulting Document on
 * `event.detail.data.html` — we look up our own re-rendered instance by id
 * and morph the contents.
 *
 * Usage:
 *   <variant-scoped-block id="MyBlock-{{ block.id }}" data-product-id="{{ product.id }}">
 *     ...liquid output...
 *   </variant-scoped-block>
 *
 * The element id MUST be unique and identical between the initial render
 * and the section-rendered HTML so the lookup succeeds.
 */
class VariantScopedBlock extends Component {
  connectedCallback() {
    super.connectedCallback();
    const target = this.closest(
      '[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, featured-product-information'
    );
    if (!target) return;
    this.eventTarget = target;
    target.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.eventTarget?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  handleVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    const productId = this.dataset.productId;
    if (
      productId &&
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== productId
    ) {
      return;
    }

    const blockId = this.dataset.blockId;
    let fresh = null;
    if (blockId) {
      fresh = html.querySelector(`variant-scoped-block[data-block-id="${CSS.escape(blockId)}"]`);
    }
    if (!fresh && this.id) {
      fresh = html.getElementById(this.id);
    }
    if (!fresh) {
      if (this.innerHTML !== '') this.innerHTML = '';
      return;
    }
    if (fresh.innerHTML === this.innerHTML) return;
    this.innerHTML = fresh.innerHTML;
  };
}

if (!customElements.get('variant-scoped-block')) {
  customElements.define('variant-scoped-block', VariantScopedBlock);
}
